import { forEach, remove } from 'lodash-es';
import { Fetch, initFetch } from '@trp-ta-nitro/http-js';
import AuthType from '../AuthType';
import * as Cookies from '../cookies';
import { ApigeeError } from '../errors';
import * as Http from '../http';
import RequestOptions from '../http/RequestOptions';
import * as Location from '../location';
import { ConfigUrl, RequestInterceptor, Type } from '../types';
import { defaultedOpt } from '../util';
import { AuthState, AuthStateListener, NewAuthState } from './AuthState';
import {
  AuthService,
  BeforeLoginHook,
  LoginBehavior,
  LoginReturnUrlBuilder,
} from './types';

export const DEFAULT_DELEGATE_USER_PARAM = 'delegateUser';
const DEFAULT_ECHO_URL = {
  path: '/architecture-reference-implementations_echo-oauth-token/echo',
  scheme: 'https',
};

/**
 *  Given a response that was not a 2xx, creates an AuthState based on the response
 *  body. There can be many causes for a non-2xx, this checks the known ones (ex: Apigee
 *  faults) and attempts to make them obvious.
 *
 * @param response
 * @param body
 */
function authResponseNotOk(response: Response, body: string): AuthState {
  const apigeeError = ApigeeError.fromResponseBody(body);
  if (apigeeError) {
    return NewAuthState.Error(apigeeError);
  }
  return NewAuthState.Reason(Http.coerceResponseErrorMessage(response, body));
}

function authResponseOk(responseBody: string): AuthState {
  let authResponse: any;
  try {
    authResponse = JSON.parse(responseBody);
  } catch (err: any) {
    throw new Error('could not parse auth response: ' + err.message);
  }
  const authState: AuthState = {
    authenticated: true,
    entitlements: authResponse.entitlements,
    userId: authResponse.uid,
  };
  if (authResponse.exp) {
    // the exp is only available from /me for internal sessions
    authState.expiresAt = authResponse.exp;
  }
  return authState;
}

async function parseAuthResponse(response: Response) {
  const body = await response.text();
  // the response is assumed to be in the format of the /v0/ciam-public/me endpoint
  if (response.status !== 200) {
    return authResponseNotOk(response, body);
  }
  return authResponseOk(body);
}

function defaultLoginBehavior(returnUrl?: string | null) {
  // this promise intentionally does not resolve or reject, it is expected that the redirect
  // will occur and the entire app will be unloaded...note that if login becomes a process that's
  // not based on a redirect (ex: logging in on the current page), then this promise should be
  // changed to resolve once login is complete
  return (authService: AuthService, location: Location) =>
    new Promise(() => {
      location.assign(authService.buildLoginUrl(returnUrl));
    });
}

export interface BaseAuthNames {
  // the name of the header in which the api key is passed
  headerApiKey?: string;
  // the name of the login page's query param for redirect url
  paramLoginReturnUrl: string;
}

export interface BaseAuthHooks {
  // a hook that is called before login, useful because many applications use the default
  // behavior of redirecting to login if the user is not authenticated, meaning they never
  // call login() directly
  beforeLogin?: BeforeLoginHook;
  // a hook that overrides entirely what the application does on login, replacing the default
  // which is to redirect to the configured login page, note that the beforeLogin hook is called
  // immediately before the login behavior, regardless of what it is
  loginBehavior?: LoginBehavior;
  // a hook that determines where to send the browser after login, a hooks is available as
  // the login process is often kicked off as part of verifyAuthentication(), so the login
  // returnUrl cannot be used
  loginReturnUrlBuilder?: LoginReturnUrlBuilder;
  // is called before a fetch request is made, allowing the request options to
  // be augmented...widely used in automated tests
  requestInterceptor?: RequestInterceptor;
}

export interface BaseAuthServiceOptions {
  // an apigee API key for interacting with the various auth endpoints
  apiKey: string;
  // an AuthType class for interacting with both this service's type and to
  // handle functions such as redirecting to other auth types for CustomerWebView
  authType?: AuthType;
  // the configured url for the ciam /me endpoint
  authUrl?: ConfigUrl;
  // the domain to use for all default url configuration
  baseDomain: string;
  // passed in Cookies interaction, not meant to be overridden, but an explicit dependency to facilitate testing
  cookies: Cookies.Cookies;
  // whether the CustomerWebView functionality is enabled or not
  customerWebViewEnabled?: boolean;
  // the configured url for the architecture-reference-implementations_echo-oauth-token/echo
  // endpoint, which facilitates local development
  echoUrl?: ConfigUrl;
  // the environment the service is running in, used to determine default values
  environment: string;
  // a fetch implementation to use
  fetch?: Fetch<RequestInit, Response>;
  // a set of overridable/pluggable function-based behavior, see their type
  // definition for more details
  hooks?: BaseAuthHooks;
  // passed in Location, not meant to be overridden, but an explicit dependency to facilitate testing
  location: Location;
  // names for various pieces of functionality (header names, param names), see
  // their type definition for more details
  names?: Partial<BaseAuthNames>;
  // whether the user will be forced to login if they are not authenticated or not
  requireAuthentication?: boolean;
}

/**
 * A base class that contains common behavior used by all current auth services.
 */
export default abstract class BaseAuthService implements AuthService {
  protected readonly authStateListeners: AuthStateListener[] = [];
  protected readonly authType: AuthType | undefined;
  protected readonly authUrl: ConfigUrl;
  protected readonly cookies: Cookies.Cookies;
  protected readonly customerWebViewEnabled: boolean;
  protected readonly environment: string;
  protected readonly fetch: Fetch<RequestInit, Response>;
  protected readonly location: Location;
  protected readonly requestOptions: RequestOptions;
  protected readonly requireAuthentication: boolean;
  private readonly baseHooks: BaseAuthHooks;
  private readonly echoUrl: ConfigUrl;
  protected lastAuthState?: AuthState;

  protected constructor(options: BaseAuthServiceOptions) {
    this.authType = options.authType;
    this.authUrl = defaultedOpt(options.authUrl);
    this.baseHooks = defaultedOpt(options.hooks);
    this.cookies = options.cookies;
    this.customerWebViewEnabled = Boolean(options.customerWebViewEnabled);
    this.echoUrl = Object.assign({}, DEFAULT_ECHO_URL, options.echoUrl);
    this.environment = options.environment;
    this.fetch = options.fetch ?? initFetch().build();
    this.location = options.location;
    this.requestOptions = new RequestOptions({
      apiKey: options.apiKey,
      apiKeyHeader: options.names?.headerApiKey,
    });
    this.requireAuthentication = Boolean(options.requireAuthentication);
  }

  protected coerceReturnUrl(returnUrl?: ConfigUrl | null) {
    if (returnUrl) {
      return returnUrl;
    }
    return this.baseHooks.loginReturnUrlBuilder?.();
  }

  /**
   * A wrapper around fetch that both applies common headers (including api key)
   * and uses an optionally configured request interceptor to augment the
   * request.
   *
   * @param url
   * @param options
   */
  protected async fetchWithInterceptor(url: string, options: RequestInit) {
    if (this.baseHooks?.requestInterceptor) {
      options = this.baseHooks.requestInterceptor(url, options);
    }
    return this.fetch(url, options);
  }

  /**
   * Hits the passed `authUrl` to determine both that the user is authenticated
   * and what their entitlements are. Returns the authentication status, and
   * if authenticated, their user id and entitlements.
   *
   * If not authenticated and there was an error, will return the error as
   * well (essentially the reason they're not authenticated).
   *
   * This assumes that the thing that authenticates the user (ex: a token) will
   * automatically be applied by the fetch, either by a browser (a cookie)
   * or the configured request interceptor.
   *
   * @param authUrl
   */
  protected async fetchAuthorization(authUrl: string): Promise<AuthState> {
    const response = await this.fetchWithInterceptor(
      authUrl,
      this.requestOptions.options().withApiKey().build(),
    );
    Http.checkResponseError(response);
    // if an unhandled error (ex: a network error) occurs it will bubble up and need to be handled
    return await parseAuthResponse(response);
  }

  protected authStateChanged(authState: AuthState) {
    for (const subscription of this.authStateListeners) {
      subscription(authState);
    }
    this.lastAuthState = authState;
    return authState;
  }

  /**
   * Used when authentication is successful to apply the auth cookies to the
   * localhost domain.
   *
   * Only applies when used with a page at localhost.
   */
  protected async localhostAuthCookies() {
    if (!Location.isLocalhost(this.location)) {
      return;
    }
    const echoUrl = Location.buildUrl(this.location, this.echoUrl);
    const response = await this.fetchWithInterceptor(
      echoUrl,
      this.requestOptions.options().withApiKey().build(),
    );
    Http.checkResponseError(response);
    const body = await response.json();
    forEach(body, (value, name) => {
      if (!value) {
        return;
      }
      this.cookies.setCookie(name, value, {
        path: '/',
      });
    });
  }

  // Overridable Behavior
  // ====================

  protected async afterAuthenticated(authState: AuthState): Promise<AuthState> {
    await this.localhostAuthCookies();
    return authState;
  }

  protected async beforeAuthentication(): Promise<void> {
    // by default there is no pre-authentication behavior
  }

  // AuthService
  // ===========

  abstract buildLoginUrl(returnUrl?: string | null): string;

  clearAuthStateListeners() {
    this.authStateListeners.splice(0, this.authStateListeners.length);
  }

  listenAuthState(listener: AuthStateListener) {
    this.authStateListeners.push(listener);
    // return a function that when called will remove the subscription from the list of subscriptions
    return () => {
      remove(this.authStateListeners, (s) => s === listener);
    };
  }

  /**
   * Uses the configured loginBehavior to perform login.
   *
   * Current default login behavior is via immediate redirect. Will redirect back to the
   * current page if login is successful.
   */
  async login(returnUrl?: string | null) {
    const loginBehavior = this.baseHooks.loginBehavior ?? defaultLoginBehavior(returnUrl);
    await this.baseHooks.beforeLogin?.();
    await loginBehavior(this, this.location);
  }

  abstract logout(gotoUrl?: string | null): Promise<void>;

  refreshToken(): Promise<AuthState> {
    throw new Error(`${this.type()} tokens cannot be explicitly refreshed`);
  }

  abstract type(): Type;

  /**
   * Performs the necessary steps to verify that the current user (based on
   * context from the browser) is authenticated as a proper user.
   *
   * The authentication workflow is to hit the ciam /me endpoint. If it returns
   * okay, the user is authenticated, and the response contains the user's id
   * and entitlements.
   */
  async verifyAuthentication(): Promise<AuthState> {
    let authState: AuthState;
    try {
      authState = await this.fetchAuthorization(
        Location.buildUrl(this.location, this.authUrl),
      );
      await this.beforeAuthentication();
      if (authState.authenticated) {
        authState = await this.afterAuthenticated(authState);
      } else if (this.requireAuthentication) {
        await this.login();
      }
    } catch (err: any) {
      authState = NewAuthState.Error(err);
    }
    return this.authStateChanged(authState);
  }
}
